/**
 * Return-to-page for sign-in (UAT L1). A guard, a sign-in prompt or a "Sign in" button sends the
 * person to `/login?next=<where they were>` and `Login` sends them back after success.
 *
 * `next` is router-relative (the router is mounted under /<city>, so `/activity` here is
 * `/<city>/activity` in the address bar) and is attacker-controllable, so `safeNext` only accepts
 * an in-app absolute path: it must start with a single "/", never "//" or "/\" (protocol-relative
 * / backslash-normalised hosts) and carry no scheme or control characters.
 */
export function safeNext(next) {
  if (typeof next !== "string" || !next) return "";
  if (!next.startsWith("/")) return "";
  if (next.startsWith("//") || next.startsWith("/\\")) return "";
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\\]/.test(next)) return "";
  if (/^\/+(login|staff-login|auth\/google)(\/|\?|#|$)/.test(next)) return "";
  return next;
}

/** R3-13: the same path with a leading `/<city>` removed. The router is mounted under the city,
 * so a `next` that already carries the prefix (`/biswan/activity`, as copied from the address bar)
 * would otherwise resolve to `/biswan/biswan/activity`, match no route and land on the city home. */
export function stripCity(next, city) {
  if (!city || typeof next !== "string") return next;
  const prefix = `/${city}`;
  if (next === prefix) return "/";
  if (next.startsWith(`${prefix}/`) || next.startsWith(`${prefix}?`) || next.startsWith(`${prefix}#`)) {
    return next.slice(prefix.length) || "/";
  }
  return next;
}

/** `/login`, or `/login?next=…` when there is a safe place to come back to.
 *
 * R3-13: pass the current `city` slug and `next` is written FULL (`/biswan/activity`) so the
 * address bar / a shared link names the page unambiguously; `Login` strips the prefix back off
 * before navigating (the router itself is city-relative). */
export function loginPath(next, city) {
  const safe = safeNext(next);
  if (!safe) return "/login";
  const full = city && stripCity(safe, city) !== "/" ? `/${city}${stripCity(safe, city)}` : safe;
  return `/login?next=${encodeURIComponent(full)}`;
}

/** The current router location as a `next` value. */
export function hereOf(location) {
  return `${location?.pathname || ""}${location?.search || ""}${location?.hash || ""}`;
}
